import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { FormikHelpers } from "formik";
import { ReactElement } from "react";
import * as Yup from "yup";

import {
  EditBudgetForSubaccountMutation,
  EditBudgetForSubaccountMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  FormikDialog,
  FormikSubmitButton,
  FormikTextField,
  submitErrorField,
} from "~frontend/components/forms";
import {
  EDIT_BUDGET_FOR_SUBACCOUNT,
  GET_LATEST_BUDGETS,
} from "~frontend/graphql/budgets";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import { formatMillicentsAsDollars } from "../pricingV1/priceFormatting";
import { BudgetCalculator } from "./BudgetCalculator";
import { DollarAdornment } from "./DollarAdornment";
import { Budget, Subaccount } from "./types";
import { formatMaybeNegativeMillicentsAsDollarsWithDollarSign } from "./util";

type EditBudgetFormValues = {
  allocation: string;
  warningThreshold: string;
  [submitErrorField]: any;
};

export function EditBudgetDialog({
  subaccount,
  budget,
  onClose,
}: {
  subaccount: Subaccount;
  budget: Budget;
  onClose: () => void;
}): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();

  const [editBudget] = useMutation<
    EditBudgetForSubaccountMutation,
    EditBudgetForSubaccountMutationVariables
  >(EDIT_BUDGET_FOR_SUBACCOUNT, {
    onErrorSnackbarMessage: "Error editing budget",
    refetchQueries: [GET_LATEST_BUDGETS],
    awaitRefetchQueries: true,
  });

  const handleSubmit = async (
    values: EditBudgetFormValues,
    formik: FormikHelpers<EditBudgetFormValues>
  ) => {
    const allocationInMillicents = Math.floor(
      parseFloat(values.allocation) * 100 * 1000
    );
    const warningThresholdInMillicents = Math.floor(
      parseFloat(values.warningThreshold) * 100 * 1000
    );

    const { errors } = await editBudget({
      variables: {
        input: {
          subaccountId: subaccount.id,
          allocationInMillicents,
          warningThresholdInMillicents,
        },
      },
    });

    if (errors) {
      formik.setErrors({ [submitErrorField]: getErrorMessage(errors) });
    } else {
      onClose();
    }
  };

  return (
    <FormikDialog
      open
      initialValues={{
        [submitErrorField]: null,
        allocation: formatMillicentsAsDollars(budget.allocationInMillicents),
        warningThreshold: formatMillicentsAsDollars(
          budget.warningThresholdInMillicents
        ),
      }}
      validationSchema={Yup.object().shape({
        allocation: Yup.number()
          .typeError("Budget must be a number")
          .min(
            budget.usageInMillicents / (1000 * 100),
            "Budget must be more than the current usage"
          )
          .required("Budget is required"),
        warningThreshold: Yup.number()
          .typeError("Warning threshold must be a number")
          .test(
            "less-than-budget",
            "Warning threshold must be less than budget",
            function (value) {
              const { allocation } = this.parent;
              return (value ?? 0) < allocation;
            }
          )
          .required("Warning threshold is required"),
      })}
      onSubmit={handleSubmit}
    >
      {({ values, isSubmitting, isValid, errors }) => {
        return (
          <>
            <DialogTitle>
              Edit Budget for <strong>{subaccount.name}</strong>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ marginBottom: "16px" }}>
                Enter the budget you would like to enforce for this subaccount,
                or use our calculator to estimate how much you'll need. You can
                edit your budget at any time, or end this budgeting period and
                reset with a new budget.
              </Typography>
              <FormikTextField
                name="allocation"
                label={
                  <FlexBox
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                  >
                    Budget
                    <Box
                      sx={{
                        backgroundColor: palette.lightOrange,
                        color: palette.navy,
                        px: "3px",
                      }}
                    >
                      <Typography variant="body1">
                        Current Spend: $
                        {formatMillicentsAsDollars(budget.usageInMillicents)} /{" "}
                        <strong>
                          New Balance:
                          {formatMaybeNegativeMillicentsAsDollarsWithDollarSign(
                            parseFloat(values.allocation) * 100 * 1000 -
                              budget.usageInMillicents
                          )}
                        </strong>
                      </Typography>
                    </Box>
                  </FlexBox>
                }
                sx={{
                  ".MuiFormLabel-root": {
                    // because the label has transform: scale(0.75) we need to set the width to
                    // 133% to make it appear as full-width
                    width: "133%",
                    maxWidth: "133%",
                  },
                }}
                InputProps={{
                  startAdornment: <DollarAdornment />,
                }}
                fullWidth
              />
              <BudgetCalculator sx={{ marginBottom: "16px" }} />
              <FormikTextField
                name="warningThreshold"
                label="Warning Threshold"
                bottomLabel="The Subaccount and Organization Admins will get an email warning when your balance falls below this amount"
                InputProps={{
                  startAdornment: <DollarAdornment />,
                }}
                fullWidth
              />
            </DialogContent>
            <DialogActions
              sx={{
                justifyContent: "space-between",
                pt: 0,
                px: "24px",
                pb: "20px",
              }}
            >
              <Box sx={{ width: "250px" }}>
                {errors[submitErrorField] && (
                  <Typography variant="body2" sx={{ color: palette.red }}>
                    {errors[submitErrorField]}
                  </Typography>
                )}
              </Box>
              <FlexBox gap="10px">
                <Button
                  disabled={isSubmitting}
                  variant="contained"
                  color="tertiary"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <FormikSubmitButton
                  disabled={!isValid}
                  submitText="Save Changes"
                  loadingText="Saving..."
                />
              </FlexBox>
            </DialogActions>
          </>
        );
      }}
    </FormikDialog>
  );
}
